import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import dynamic from 'next/dynamic'
import type {TestimonialsSectionSliceTestimonialVideos} from 'prismicio-types'
import {useState} from 'react'
import {Icon} from 'src/common/Icon'
import {ModalVideoPlayer} from 'src/common/ModalVideoPlayer'
import {EVENTS, getLinkUrl, getMediaLinkUrl, track} from 'src/common/helpers'
import styles from './videoStyles.module.css'

const VideoPlayer = dynamic({
  loader: () => import('src/common/VideoPlayer').then((mod) => mod.VideoPlayer),
  ssr: false,
})

type TestimonialsVideosVariationProps = {
  slice: TestimonialsSectionSliceTestimonialVideos
  componentIndex: number
  pageUid: string
}

export const TestimonialsVideosVariation = ({slice, componentIndex, pageUid}: TestimonialsVideosVariationProps) => {
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null)

  const testimonialVideoUrls = slice.primary.testimonials.map((testimonial) =>
    isFilled.embed(testimonial.video_link) ? testimonial.video_link.embed_url : '',
  )

  const backgroundVideoUrl = getMediaLinkUrl(slice.primary.background_video)
  const mainVideoUrl = getLinkUrl(slice.primary.play_main_video_button)

  const handleClick = (index: number, videoUrl: string) => {
    track({
      event: EVENTS.CLICK,
      mktgId: `testimonial-video-button-${index}`,
      location: `testimonial-video-${componentIndex}`,
      pageName: pageUid,
      dataType: 'video-button',
    })

    setActiveVideoUrl(videoUrl)
  }

  return (
    <section className="relative isolate flex flex-col max-md:pt-[300px] md:h-[100vh] md:max-h-[800px] md:justify-end lg:h-[800px]">
      <div className="absolute inset-0 z-0">
        <VideoPlayer
          aspectRatio="aspect-auto"
          className={`h-full !max-w-none !rounded-none max-md:h-1/2 ${styles.videoTest}`}
          src={backgroundVideoUrl}
          autoplay
          loop
          hideControls
        />
      </div>
      <div
        className="z-10 mx-auto flex w-full max-w-[1200px] flex-col gap-8 px-5 pb-10 max-md:bg-grey-gradient md:px-10 md:pb-20 lg:px-5 xl:px-0"
        data-aos="fade-up"
      >
        <div className="flex gap-6 max-md:flex-col md:justify-between">
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({children}) => (
                <h3 className="lg:text-tg-h2 text-32 leading-[normal] tracking-[-.32px] text-moss-white">{children}</h3>
              ),
              strong: ({children}) => <span className="text-moss-white/[70%]">{children}</span>,
            }}
          />
          {mainVideoUrl && (
            <button
              onClick={() => handleClick(slice.primary.testimonials.length, mainVideoUrl)}
              className="text-button-s flex !h-12 w-fit items-center gap-2.5 rounded-[4px] bg-moss-white/[30%] p-4 text-moss-white backdrop-blur-[6px] transition-all duration-300 ease-in-out hover:brightness-110 md:self-end"
            >
              <Icon name="play" className="size-3 text-moss-white" />
              {slice.primary.play_main_video_button.text}
            </button>
          )}
        </div>
        <div className="flex gap-6 max-md:flex-col">
          {slice.primary.testimonials.map((testimonial, index) => (
            <div
              key={`testimonial-${index}`}
              className="group/video-card flex cursor-pointer flex-col gap-3 rounded-lg bg-moss-white p-6 hover:animate-bounce-smooth md:max-lg:p-4"
              onClick={() => handleClick(index, testimonialVideoUrls[index])}
            >
              <div className="relative">
                <PrismicNextImage field={testimonial.image} className="aspect-[56/39] rounded-[6px]" />
                <span className="text-detail absolute left-[14px] top-[13px] rounded-[4px] bg-moss-white/[20%] px-2.5 py-[3px] font-medium text-moss-white backdrop-blur-[2px]">
                  {testimonial.video_duration}
                </span>
              </div>
              <div className="flex w-full">
                <div className="flex w-full flex-col gap-1.5">
                  <PrismicRichText
                    field={testimonial.text}
                    components={{
                      paragraph: ({children}) => (
                        <div className="flex items-center text-18 font-medium leading-6 text-moss-black">
                          {children}
                        </div>
                      ),
                      strong: ({children}) => <span className="text-button-s text-moss-black/[60%]">{children}</span>,
                      em: () => <div className="mx-2 h-[5px] w-[5px] min-w-[5px] rounded-[1px] bg-moss-grey-200" />,
                    }}
                  />
                </div>
                <div className="flex size-8 min-w-8 items-center justify-center place-self-end rounded-full bg-moss-black transition-all duration-300 ease-in-out group-hover/video-card:bg-moss-black/[70%]">
                  <Icon name="play" className="h-[15px] w-[15px] min-w-[15px] text-moss-white" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {activeVideoUrl === null ? null : (
        <ModalVideoPlayer
          className="h-auto lg:h-[66.25vh]"
          onClose={() => setActiveVideoUrl(null)}
          src={activeVideoUrl}
          autoplay
        />
      )}
    </section>
  )
}
